/**
 * 제약사 정산 정보 목록.
 * admin-ruda-web `settlementUpload.js` 포팅.
 */
import {
  usePharmaCompaniesForSettlement,
  useSettlementMonths,
  useSettlementPharmaList,
  useSettlementStatus,
  useUploadPharmaSettlements,
} from '@/entities/settlement/hooks/useSettlements';
import { EmptyState } from '@/shared/components/EmptyState';
import { DataCard } from '@/shared/components/DataCard';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { MonthPicker } from '@/shared/components/DatePicker';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { settlementUploadDetailPath, ROUTE_PATHS } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { SettlementStatusBanner } from '@/pages/settlements/SettlementStatusBanner';
import {
  formatDateTime,
  formatSettlementYearMonth,
  getDefaultSettlementYm,
} from '@/pages/settlements/settlementUtils';
import {
  downloadSettlementUploadTemplate,
  parseSettlementUploadFile,
  SETTLEMENT_UPLOAD_ACCEPT,
} from '@/pages/settlements/settlementUploadUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, Download, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FooterRow, ToolbarRow } from '@/shared/components/ui/row';

const ALL_PHARMA = 'all';

const SETTLEMENT_UPLOAD_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  yearMonth: getDefaultSettlementYm(),
  pharmaCompanyId: '',
};

const getPharmaOptionKey = (
  pharmaCompanyId: number,
  pharmaCompanyName: string,
  index: number,
) => `${pharmaCompanyId}-${pharmaCompanyName}-${index}`;

const getSettlementRowKey = (
  row: {
    pharmaCompanyId: number;
    settlementYearMonth: string;
    prescriptionYearMonth: string;
    dataEnteredAt: string;
    pharmaCompanyName: string;
  },
  index: number,
) =>
  [
    row.pharmaCompanyId,
    row.settlementYearMonth,
    row.prescriptionYearMonth,
    row.dataEnteredAt,
    row.pharmaCompanyName,
    index,
  ].join('-');

export const SettlementUploadPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { values, setValues } = useListSearchParams(SETTLEMENT_UPLOAD_SEARCH_DEFAULTS);

  const { data: settlementMonths = [] } = useSettlementMonths();
  const didNormalizeMonthRef = useRef(false);

  // 최초 진입 시에만: 기본 년월이 서버 가능 월에 없으면 첫 유효 월로 맞춤
  useEffect(() => {
    if (didNormalizeMonthRef.current) return;
    if (settlementMonths.length === 0) return;
    didNormalizeMonthRef.current = true;

    if (settlementMonths.includes(values.yearMonth)) return;

    const preferred = settlementMonths.includes(getDefaultSettlementYm())
      ? getDefaultSettlementYm()
      : settlementMonths[0];

    setValues({
      yearMonth: preferred,
      page: 1,
    });
  }, [settlementMonths, setValues, values.yearMonth]);

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedCount, setParsedCount] = useState(0);

  const uploadMutation = useUploadPharmaSettlements();

  // 레거시 selectedMonth: 피커(년월) 변경 즉시 목록·상태·제약사 조회
  const { data: pharmaCompanies } = usePharmaCompaniesForSettlement(
    values.yearMonth || null,
  );
  const { data: status, isLoading: statusLoading } = useSettlementStatus(
    values.yearMonth || null,
  );

  const { data, isLoading, isError, error, refetch } =
    useSettlementPharmaList({
      yearMonth: values.yearMonth,
      pharmaCompanyId: values.pharmaCompanyId
        ? Number(values.pharmaCompanyId)
        : undefined,
      pageable,
    });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  /** 먼스피커 변경 = 즉시 조회 (검색 버튼 불필요) */
  const handleYearMonthChange = (next: string) => {
    setValues({
      yearMonth: next.trim() || getDefaultSettlementYm(),
      page: 1,
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      yearMonth: values.yearMonth || getDefaultSettlementYm(),
      page: 1,
    });
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setParsedCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      resetUploadState();
      return;
    }

    try {
      const rows = await parseSettlementUploadFile(file);
      setSelectedFile(file);
      setParsedCount(rows.length);
    } catch (err) {
      resetUploadState();
      const message = err instanceof Error ? err.message : '파일 파싱 실패';
      toast.error(message);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const uploadRows = await parseSettlementUploadFile(selectedFile);
      await uploadMutation.mutateAsync(uploadRows);
      toast.success('정산 데이터 업로드를 완료했습니다.');
      setUploadOpen(false);
      resetUploadState();
      await refetch();
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const yearMonthField = (
    <div className="space-y-2">
      <Label htmlFor="settlementYm">정산 년월</Label>
      <MonthPicker
        id="settlementYm"
        value={values.yearMonth}
        onChange={handleYearMonthChange}
        placeholder="년월 선택"
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Banknote}
        title="제약사 정산 정보"
        description="제약사 정산 정보 업로드 및 관리"
        actions={
          <>
            {/* 레거시 settlementUpload 「정산 정보 종합」 — 메뉴 대신 여기서 생성기 진입 */}
            <MenuWriteGate>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate({ to: ROUTE_PATHS.SETTLEMENT_GENERATOR as never })
                }
              >
                정산 정보 종합
              </Button>
            </MenuWriteGate>
            <MenuWriteGate>
              <Button size="sm" onClick={() => setUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                제약사 정산 정보 업로드
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <FilterCard>
          <form
            onSubmit={handleSearch}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {yearMonthField}
            <div className="space-y-2">
              <Label>제약사</Label>
              <Select 
                value={values.pharmaCompanyId || ALL_PHARMA} 
                onValueChange={(v) => setValues({ 
                  pharmaCompanyId: v === ALL_PHARMA ? '' : v, 
                  page: 1 
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="전체" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_PHARMA}>전체</SelectItem>
                  {(pharmaCompanies ?? []).map((p, index) => (
                    <SelectItem
                      key={getPharmaOptionKey(
                        p.pharmaCompanyId,
                        p.pharmaCompanyName,
                        index,
                      )}
                      value={String(p.pharmaCompanyId)}
                    >
                      {p.pharmaCompanyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="ruda-button w-full sm:w-auto">
                검색
              </Button>
            </div>
          </form>
      </FilterCard>

      <SettlementStatusBanner
        settlementYearMonth={values.yearMonth}
        status={status}
        isLoading={statusLoading}
      />

      <DataCard>
          {isLoading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <EmptyState description="제약사 정산 정보가 없습니다." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>제약사명</TableHead>
                    <TableHead>정산 년월</TableHead>
                    <TableHead>처방 년 월</TableHead>
                    <TableHead>제약사 정산 정보 입력일시</TableHead>
                    <TableHead>파일 업로드 상태</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={getSettlementRowKey(row, index)}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        navigate({
                          to: settlementUploadDetailPath(row.pharmaCompanyId),
                          search: {
                            settlementYm: row.settlementYearMonth,
                            prescriptionYm: row.prescriptionYearMonth,
                          },
                        } as never)
                      }
                    >
                      <TableCell>{row.pharmaCompanyName}</TableCell>
                      <TableCell>
                        {formatSettlementYearMonth(row.settlementYearMonth)}
                      </TableCell>
                      <TableCell>
                        {formatSettlementYearMonth(row.prescriptionYearMonth)}
                      </TableCell>
                      <TableCell>{formatDateTime(row.dataEnteredAt)}</TableCell>
                      <TableCell>
                        <Badge variant={row.isUploaded ? 'default' : 'secondary'}>
                          {row.isUploaded ? '완료' : '미완료'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <SimplePagination
                page={values.page}
                pageSize={values.pageSize}
                total={totalCount}
                onPageChange={(page) => setValues({ page })}
                onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
              />
            </>
          )}
      </DataCard>

      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) resetUploadState();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>제약사 정산 정보 업로드</DialogTitle>
            <DialogDescription>
              양식(CSV·엑셀)에 맞춘 제약사 정산 정보 파일을 선택한 뒤
              업로드합니다.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <ToolbarRow gap={8}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadSettlementUploadTemplate}
              >
                <Download className="mr-2 h-4 w-4" />
                양식 다운로드
              </Button>
              <FileUploadButton
                inputId="settlement-upload-file"
                inputRef={fileInputRef}
                accept={SETTLEMENT_UPLOAD_ACCEPT}
                resetOnSelect={false}
                onChange={handleFileChange}
              >
                파일 선택
              </FileUploadButton>
            </ToolbarRow>
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                {selectedFile.name} — {parsedCount}건
              </p>
            )}
          </div>
          <FooterRow>
            <Button
              variant="outline"
              onClick={() => {
                setUploadOpen(false);
                resetUploadState();
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleUpload}
              disabled={
                !selectedFile || parsedCount === 0 || uploadMutation.isPending
              }
            >
              {uploadMutation.isPending ? '업로드 중...' : '업로드'}
            </Button>
          </FooterRow>
        </DialogContent>
      </Dialog>
    </div>
  );
};
